/**
 * Exportación SUENLACE A3 para FACTURAS RECIBIDAS a partir de un Excel del ERP
 * (flujo "Convertir Excel" en Recibidas). No tiene nada que ver con el flujo
 * PDF+OCR: aquí el usuario ya trae los datos limpios en filas y queremos
 * generar el .DAT tal cual.
 *
 * Base: `a3ecoExportIssued.ts`. Diferencias frente a Ventas:
 *   - Reg "1"/"2" pos 58 = "2"  (Compras, no Ventas)
 *   - Reg "9": IVA soportado en pos 192 salvo autorrepercusión, donde A3
 *     interpreta pos 192 como Haber (477) y pos 228 como Debe (472).
 *   - Subtipo IVA (pos 100-101) según operationType (interior=01, aib=03, …)
 *   - Reg "V" de vencimiento se OMITE (igual que emitidas: el cliente prefiere
 *     no traspasar vencimientos desde Excel — los gestiona en A3).
 */

import { parseNumericLike } from "@/lib/numberFormatting";
import type { SupplierOperationType } from "@/lib/supplierMaster";

const LINE_LENGTH = 512;
const PAYLOAD_LENGTH = 510;
const FORMAT_TAG = "5";
const CURRENCY_TAG = "E";
const GENERATED_TAG = "N";
const WIN1252_FALLBACK = 0x3f;

const padLeft = (v: string, w: number, c = "0") =>
  v.length >= w ? v.slice(-w) : c.repeat(w - v.length) + v;
const padRight = (v: string, w: number, c = " ") =>
  v.length >= w ? v.slice(0, w) : v + c.repeat(w - v.length);
const cleanText = (s: string | null | undefined) =>
  (s ?? "").replace(/[\r\n\t]/g, " ").replace(/\s+/g, " ").trim();

const padAccount12 = (s: string | null | undefined): string => {
  const digits = (s ?? "").replace(/[^0-9]/g, "");
  if (!digits) return padRight("", 12);
  const acc = digits.length > 12 ? digits.slice(0, 12) : digits;
  return padRight(acc, 12);
};

const buildVatAccount = (root: string, referenceAccounts: (string | null | undefined)[]): string => {
  if (!root) return "";
  const lengths = referenceAccounts
    .map((a) => (a ?? "").replace(/[^0-9]/g, "").length)
    .filter((n) => n >= 6 && n <= 12);
  const targetLen = lengths.length > 0 ? Math.max(...lengths) : 8;
  return root.padEnd(targetLen, "0");
};

const OP_TYPE_CONFIG: Record<SupplierOperationType, { subtipo: string; repercutidoRoot: string; soportadoRoot: string }> = {
  interior:    { subtipo: "01", repercutidoRoot: "",    soportadoRoot: "472" },
  aib:         { subtipo: "03", repercutidoRoot: "477", soportadoRoot: "472" },
  ais:         { subtipo: "08", repercutidoRoot: "477", soportadoRoot: "472" },
  isp:         { subtipo: "04", repercutidoRoot: "477", soportadoRoot: "472" },
  importacion: { subtipo: "06", repercutidoRoot: "",    soportadoRoot: "472" },
};

const formatA3Amount = (amount: number): string => {
  const sign = amount < 0 ? "-" : "+";
  const abs = Math.abs(amount);
  const intPart = Math.floor(abs);
  const decPart = Math.round((abs - intPart) * 100);
  if (decPart >= 100) return sign + padLeft(String(intPart + 1), 10) + "." + "00";
  return sign + padLeft(String(intPart), 10) + "." + padLeft(String(decPart), 2);
};

const formatA3Rate = (rate: number): string => {
  const abs = Math.abs(rate);
  const intPart = Math.floor(abs);
  const decPart = Math.round((abs - intPart) * 100);
  if (decPart >= 100) return padLeft(String(intPart + 1), 2) + "." + "00";
  return padLeft(String(intPart), 2) + "." + padLeft(String(decPart), 2);
};

const formatA3Date = (iso: string): string => {
  if (!iso) return "        ";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}${m[2]}${m[3]}`;
  const m2 = iso.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (m2) return `${m2[3]}${m2[2]}${m2[1]}`;
  return "        ";
};

const A3_COUNTRY_NUMERIC: Record<string, string> = {
  ES: "011", FR: "109", DE: "067", IT: "103", PT: "165", IE: "153",
  NL: "137", BE: "017", AT: "002", LU: "129", DK: "057", SE: "203",
  FI: "077", PL: "163", CZ: "055", SK: "189", HU: "099", RO: "175",
  GR: "087", EL: "087", BG: "043", HR: "053", SI: "187", EE: "071",
  LV: "117", LT: "121", MT: "133", CY: "049", GB: "069", XI: "069",
  CH: "047", NO: "143", US: "207", MX: "131",
};

const detectForeignPrefix = (raw: string | null | undefined) => {
  const id = (raw ?? "").toUpperCase().replace(/[^0-9A-Z]/g, "");
  if (id.length < 4) return null;
  const prefix = id.slice(0, 2);
  if (prefix === "ES") return null;
  if (!/^[A-Z]{2}$/.test(prefix)) return null;
  if (!A3_COUNTRY_NUMERIC[prefix]) return null;
  return { country: prefix, rest: id.slice(2) };
};

const cleanTaxId = (taxId: string | null | undefined): string => {
  const raw = cleanText(taxId).toUpperCase().replace(/[\s.\-/]/g, "");
  if (raw.startsWith("ES") && raw.length === 11) return raw.slice(2);
  return raw;
};

const buildDocumentNumber = (invoiceNumber: string, series?: string): string => {
  const s = cleanText(series ?? "").toUpperCase();
  const inv = cleanText(invoiceNumber).toUpperCase();
  if (inv && s && !inv.startsWith(s)) return padRight(`${s}${inv}`.slice(0, 10), 10);
  const doc = inv || s;
  return doc ? padRight(doc.slice(0, 10), 10) : padRight("", 10);
};

/** Documento para pos 59 cuando venga el nº de registro IVA: se pasa tal cual, sin rellenar con ceros. */
const buildLedgerDocumentNumber = (ledger: string): string => {
  const clean = cleanText(ledger).toUpperCase();
  if (!clean) return padRight("", 10);
  return padRight(clean.slice(0, 10), 10);
};

const invoiceConcept = (invoiceNumber: string): string =>
  `S/Fra. ${cleanText(invoiceNumber).toUpperCase()}`.slice(0, 30);

const encodeWin1252 = (text: string): Uint8Array => {
  const bytes: number[] = [];
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (code < 0x80) bytes.push(code);
    else if (code >= 0xa0 && code <= 0xff) bytes.push(code);
    else {
      switch (code) {
        case 0x20ac: bytes.push(0x80); break;
        case 0x201a: bytes.push(0x82); break;
        case 0x0192: bytes.push(0x83); break;
        case 0x201e: bytes.push(0x84); break;
        case 0x2026: bytes.push(0x85); break;
        case 0x2020: bytes.push(0x86); break;
        case 0x2021: bytes.push(0x87); break;
        case 0x02c6: bytes.push(0x88); break;
        case 0x2030: bytes.push(0x89); break;
        case 0x0160: bytes.push(0x8a); break;
        case 0x2018: bytes.push(0x91); break;
        case 0x2019: bytes.push(0x92); break;
        case 0x201c: bytes.push(0x93); break;
        case 0x201d: bytes.push(0x94); break;
        case 0x2022: bytes.push(0x95); break;
        case 0x2013: bytes.push(0x96); break;
        case 0x2014: bytes.push(0x97); break;
        default: bytes.push(WIN1252_FALLBACK);
      }
    }
  }
  return new Uint8Array(bytes);
};

const buildLine = (fields: { pos: number; value: string }[]): string => {
  const buf = new Array(PAYLOAD_LENGTH).fill(" ");
  buf[0] = FORMAT_TAG;
  buf[508] = CURRENCY_TAG;
  buf[509] = GENERATED_TAG;
  for (const f of fields) {
    const idx = f.pos - 1;
    for (let i = 0; i < f.value.length && idx + i < PAYLOAD_LENGTH; i++) {
      buf[idx + i] = f.value[i];
    }
  }
  return buf.join("");
};

// ── Tipos públicos ────────────────────────────────────────────────────────

export interface ReceivedInvoiceRow {
  /** Fecha del asiento contable. */
  date: string;
  /** Fecha original de la factura del proveedor. */
  invoiceDate: string;
  series?: string;
  invoiceNumber: string;
  /** Nº de registro IVA (orden contable). Si se informa, va a documento/nº factura A3 y el invoiceNumber pasa al concepto. */
  vatLedgerNumber?: string;
  supplierName: string;
  supplierTaxId: string;
  supplierAccount: string;
  expenseAccount: string;
  vatLines: {
    taxableBase: number;
    vatRate: number;
    vatAmount: number;
    surchargeRate?: number;
    surchargeAmount?: number;
  }[];
  irpfRate?: number | null;
  irpfAmount?: number | null;
  totalAmount: number;
  postalCode?: string;
  operationType?: SupplierOperationType;
  /** Forma de pago tal como viene del ERP ("CAJA", "TARJETA", "TRANSFERENCIA"…). */
  paymentMethod?: string;
}

export interface ReceivedExportOptions {
  companyCode: string;
  /** Mapa forma de pago (UPPER sin acentos) → cuenta de tesorería (570/571…). */
  paymentAccountMap?: Record<string, string>;
  /** Si false, no genera el asiento de pago aunque haya mapa. Default: true. */
  generatePaymentEntry?: boolean;
  /** Ajuste por asesoría: usar la fecha de la factura como fecha de contabilización. */
  useInvoiceDateAsEntryDate?: boolean;
  /** Ajuste por asesoría: no traspasar el nº de registro de IVA. */
  omitVatLedgerNumber?: boolean;
}

export interface ReceivedBuildResult {
  bytes: Uint8Array;
  fileName: string;
  invoiceCount: number;
  lineCount: number;
  warnings: string[];
}

export const getReceivedVatAccount = (row: Pick<ReceivedInvoiceRow, "expenseAccount" | "supplierAccount" | "vatLines" | "operationType">): string => {
  const hasVat = (row.vatLines || []).some(
    (line) => Math.abs(parseNumericLike(line.vatAmount)) > 0 || Math.abs(parseNumericLike(line.vatRate)) > 0,
  );
  if (!hasVat) return "";
  const cfg = OP_TYPE_CONFIG[row.operationType ?? "interior"] ?? OP_TYPE_CONFIG.interior;
  return buildVatAccount(cfg.soportadoRoot || "472", [row.expenseAccount, row.supplierAccount]);
};

// ── Registros ─────────────────────────────────────────────────────────────

const buildSupplierAccountRecord = (
  empresa: string,
  date: string,
  supplierAccount: string,
  supplierName: string,
  taxId: string,
  postalCode: string,
): string => {
  const foreign = detectForeignPrefix(taxId);
  const country = foreign ? A3_COUNTRY_NUMERIC[foreign.country] : "011";
  const fields: { pos: number; value: string }[] = [
    { pos: 2, value: padLeft(empresa, 5) },
    { pos: 7, value: formatA3Date(date) },
    { pos: 15, value: "C" },
    { pos: 16, value: padAccount12(supplierAccount) },
    { pos: 28, value: padRight(cleanText(supplierName).toUpperCase(), 30) },
    { pos: 58, value: "N" },
    { pos: 59, value: formatA3Amount(0) },
    { pos: 73, value: " " },
    { pos: 78, value: padRight(cleanTaxId(taxId), 14) },
    { pos: 175, value: country },
  ];
  const cp = (postalCode || "").replace(/\D/g, "");
  if (cp.length > 0) fields.push({ pos: 155, value: padLeft(cp.slice(0, 5), 5) });
  return buildLine(fields);
};

const buildInvoiceHeader = (
  empresa: string,
  date: string,
  isRectificativa: boolean,
  supplierAccount: string,
  supplierName: string,
  documentNumber: string,
  totalAmount: number,
  taxId: string,
  concept: string,
  invoiceNumber: string,
  postalCode: string,
  invoiceDate: string,
): string => {
  const cleaned = cleanTaxId(taxId);
  const foreign = detectForeignPrefix(taxId);
  const fields: { pos: number; value: string }[] = [
    { pos: 2, value: padLeft(empresa, 5) },
    { pos: 7, value: formatA3Date(date) },
    { pos: 15, value: isRectificativa ? "2" : "1" },
    { pos: 16, value: padAccount12(supplierAccount) },
    { pos: 28, value: padRight(cleanText(supplierName).toUpperCase(), 30) },
    { pos: 58, value: "2" }, // Compras
    { pos: 59, value: documentNumber },
    { pos: 69, value: "I" },
    { pos: 70, value: padRight(concept, 30) },
    { pos: 100, value: formatA3Amount(totalAmount) },
    { pos: 176, value: padRight(cleaned, 14) },
    { pos: 190, value: padRight(cleanText(supplierName).toUpperCase(), 40) },
    { pos: 245, value: formatA3Date(invoiceDate || date) },
    { pos: 253, value: padRight(cleanText(invoiceNumber).toUpperCase(), 60) },
    { pos: 374, value: foreign ? foreign.country : "  " },
  ];
  const cp = (postalCode || "").replace(/\D/g, "");
  if (cp.length > 0) fields.push({ pos: 230, value: padLeft(cp.slice(0, 5), 5) });
  return buildLine(fields);
};

const buildVatDetail = (
  empresa: string,
  date: string,
  expenseAccount: string,
  supplierAccount: string,
  documentNumber: string,
  concept: string,
  marker: "M" | "U",
  base: number,
  rate: number,
  cuota: number,
  surchargeRate: number,
  surchargeAmount: number,
  irpfRate: number,
  irpfAmount: number,
  operationType: SupplierOperationType,
): string => {
  const cfg = OP_TYPE_CONFIG[operationType] ?? OP_TYPE_CONFIG.interior;
  const refAccs = [expenseAccount, supplierAccount];
  const soportado = buildVatAccount(cfg.soportadoRoot || "472", refAccs);
  const repercutido = buildVatAccount(cfg.repercutidoRoot, refAccs);
  const fields: { pos: number; value: string }[] = [
    { pos: 2, value: padLeft(empresa, 5) },
    { pos: 7, value: formatA3Date(date) },
    { pos: 15, value: "9" },
    { pos: 16, value: padAccount12(expenseAccount) },
    { pos: 28, value: padRight("COMPRAS", 30) },
    { pos: 58, value: "C" },
    { pos: 59, value: documentNumber },
    { pos: 69, value: marker },
    { pos: 70, value: padRight(concept, 30) },
    { pos: 100, value: cfg.subtipo },
    { pos: 102, value: formatA3Amount(base) },
    { pos: 116, value: formatA3Rate(rate) },
    { pos: 121, value: formatA3Amount(cuota) },
    { pos: 135, value: formatA3Rate(surchargeRate) },
    { pos: 140, value: formatA3Amount(surchargeAmount) },
    { pos: 154, value: formatA3Rate(irpfRate) },
    { pos: 159, value: formatA3Amount(irpfAmount) },
    { pos: 173, value: irpfAmount > 0 || irpfRate > 0 ? "05" : "01" },
    { pos: 175, value: "S" },
  ];
  // Ajuste contrastado con el traspaso real de A3 del 20/10/2026:
  // con autorrepercusión, pos 192 = Haber (477), pos 228 = Debe (472).
  // Pos 216 permanece reservada a RETENCIÓN, nunca a cuentas de IVA.
  const isAutoRepercussion = operationType === "aib" || operationType === "ais" || operationType === "isp";
  const account192 = isAutoRepercussion ? repercutido : soportado;
  const account228 = isAutoRepercussion ? soportado : repercutido;
  if (account192) fields.push({ pos: 192, value: padRight(account192, 12) });
  if (account228) fields.push({ pos: 228, value: padRight(account228, 12) });
  return buildLine(fields);
};

/** Normaliza forma de pago para lookup: mayúsculas, sin acentos, sin espacios extra. */
export const normalizePaymentMethod = (raw: string | null | undefined): string =>
  (raw || "")
    .toString()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Z0-9]+/g, " ")
    .trim();

const buildSimpleEntryRecord = (
  empresa: string,
  date: string,
  account: string,
  description: string,
  side: "D" | "H",
  reference: string,
  marker: string,
  concept: string,
  amount: number,
): string =>
  buildLine([
    { pos: 2,   value: padLeft(empresa, 5) },
    { pos: 7,   value: formatA3Date(date) },
    { pos: 15,  value: "0" },
    { pos: 16,  value: padAccount12(account) },
    { pos: 28,  value: padRight(cleanText(description).toUpperCase(), 30) },
    { pos: 58,  value: side },
    { pos: 59,  value: reference },
    { pos: 69,  value: marker },
    { pos: 70,  value: padRight(concept, 30) },
    { pos: 100, value: formatA3Amount(Math.abs(amount)) },
  ]);

const buildPaymentDueDateRecord = (
  empresa: string,
  date: string,
  supplierAccount: string,
  supplierName: string,
  documentNumber: string,
  invoiceNumber: string,
  totalAmount: number,
): string => {
  const concept = `VTO FRA/${cleanText(invoiceNumber).toUpperCase()}`.slice(0, 30);
  return buildLine([
    { pos: 2,   value: padLeft(empresa, 5) },
    { pos: 7,   value: formatA3Date(date) },
    { pos: 15,  value: "V" },
    { pos: 16,  value: padAccount12(supplierAccount) },
    { pos: 28,  value: padRight(cleanText(supplierName).toUpperCase(), 30) },
    { pos: 58,  value: "P" }, // Pago (factura recibida)
    { pos: 59,  value: documentNumber },
    { pos: 69,  value: " " },
    { pos: 70,  value: padRight(concept, 30) },
    { pos: 100, value: formatA3Amount(totalAmount) },
    { pos: 114, value: formatA3Date(date) },
  ]);
};

// ── API pública ───────────────────────────────────────────────────────────

export const validateReceivedRows = (rows: ReceivedInvoiceRow[]): string[] => {
  const warnings: string[] = [];
  for (const r of rows) {
    const id = r.invoiceNumber || "(s/n)";
    if (!r.supplierTaxId) warnings.push(`Falta NIF del proveedor en factura ${id}`);
    if (!r.supplierAccount) warnings.push(`Falta cuenta de proveedor en factura ${id} (${r.supplierName})`);
    if (!r.expenseAccount) warnings.push(`Falta cuenta de gasto en factura ${id} (${r.supplierName})`);
    if (!r.invoiceDate) warnings.push(`Falta fecha en factura ${id}`);
    if (!r.vatLines || r.vatLines.length === 0) warnings.push(`Sin líneas de IVA en factura ${id}`);
    const sumBase = (r.vatLines || []).reduce((s, l) => s + parseNumericLike(l.taxableBase), 0);
    const sumVat = (r.vatLines || []).reduce((s, l) => s + parseNumericLike(l.vatAmount), 0);
    const sumRec = (r.vatLines || []).reduce((s, l) => s + parseNumericLike(l.surchargeAmount ?? 0), 0);
    const irpf = parseNumericLike(r.irpfAmount ?? 0);
    const expected = sumBase + sumVat + sumRec - irpf;
    if (Math.abs(expected - parseNumericLike(r.totalAmount)) > 0.05) {
      warnings.push(
        `Descuadre en factura ${id}: base+IVA+RE−IRPF = ${expected.toFixed(2)} € pero total = ${parseNumericLike(r.totalAmount).toFixed(2)} €`,
      );
    }
  }
  return warnings;
};

export const buildReceivedSuenlaceFile = (
  rows: ReceivedInvoiceRow[],
  options: ReceivedExportOptions,
): ReceivedBuildResult => {
  const empresa = padLeft((options.companyCode || "").replace(/\D/g, ""), 5);
  const warnings = validateReceivedRows(rows);
  const lines: string[] = [];
  const seenSuppliers = new Set<string>();
  const skipped: string[] = [];

  for (const r of rows) {
    if (!r.supplierAccount || !r.expenseAccount) {
      skipped.push(
        `Factura ${r.invoiceNumber || "(s/n)"} (${r.supplierName || "s/nombre"}) omitida: ` +
          (!r.supplierAccount ? "falta cuenta de proveedor" : "falta cuenta de gasto"),
      );
      continue;
    }
    const total = parseNumericLike(r.totalAmount);
    const isRectificativa = total < 0;
    const totalForA3 = Math.abs(total);
    const entryDate = options.useInvoiceDateAsEntryDate ? (r.invoiceDate || r.date) : r.date;
    const ledger = options.omitVatLedgerNumber ? "" : cleanText(r.vatLedgerNumber ?? "");
    const documentNumber = ledger
      ? buildLedgerDocumentNumber(ledger)
      : buildDocumentNumber(r.invoiceNumber, r.series);
    // Si viene nº registro IVA: concepto = "S/Fra. <nº factura proveedor>"; pos 253 = nº registro IVA.
    // Si no: comportamiento anterior.
    const concept = invoiceConcept(r.invoiceNumber);
    const headerInvoiceNumber = ledger || r.invoiceNumber;
    const opType = r.operationType ?? "interior";

    const key = `${r.supplierAccount}|${cleanTaxId(r.supplierTaxId)}`;
    if (!seenSuppliers.has(key)) {
      seenSuppliers.add(key);
      lines.push(
        buildSupplierAccountRecord(
          empresa,
          entryDate,
          r.supplierAccount,
          r.supplierName,
          r.supplierTaxId,
          r.postalCode || "",
        ),
      );
    }

    lines.push(
      buildInvoiceHeader(
        empresa,
        entryDate,
        isRectificativa,
        r.supplierAccount,
        r.supplierName,
        documentNumber,
        totalForA3,
        r.supplierTaxId,
        concept,
        headerInvoiceNumber,
        r.postalCode || "",
        r.invoiceDate,
      ),
    );

    const vatLines = r.vatLines.length > 0
      ? r.vatLines
      : [{ taxableBase: 0, vatRate: 0, vatAmount: 0, surchargeRate: 0, surchargeAmount: 0 }];
    const irpfAbs = Math.abs(parseNumericLike(r.irpfAmount ?? 0));
    const irpfRate = irpfAbs > 0 && (r.irpfRate ?? 0) > 0
      ? Math.round(Math.abs(r.irpfRate!) * 100) / 100
      : irpfAbs > 0 && vatLines.length > 0
      ? Math.round((irpfAbs / vatLines.reduce((s, l) => s + Math.abs(parseNumericLike(l.taxableBase)), 0)) * 10000) / 100
      : 0;

    for (let i = 0; i < vatLines.length; i++) {
      const v = vatLines[i];
      const isLast = i === vatLines.length - 1;
      lines.push(
        buildVatDetail(
          empresa,
          entryDate,
          r.expenseAccount,
          r.supplierAccount,
          documentNumber,
          concept,
          isLast ? "U" : "M",
          Math.abs(parseNumericLike(v.taxableBase)),
          parseNumericLike(v.vatRate),
          Math.abs(parseNumericLike(v.vatAmount)),
          parseNumericLike(v.surchargeRate ?? 0),
          Math.abs(parseNumericLike(v.surchargeAmount ?? 0)),
          isLast ? irpfRate : 0,
          isLast ? irpfAbs : 0,
          opType,
        ),
      );
    }

    // Asiento de pago condicional según forma de pago (CAJA/TARJETA/TRANSFERENCIA…).
    // Debe: cuenta del proveedor / Haber: cuenta de tesorería (por el total factura).
    const genPayment = options.generatePaymentEntry !== false;
    const paymentMap = options.paymentAccountMap || {};
    const paymentKey = normalizePaymentMethod(r.paymentMethod);
    const treasuryAccount = paymentKey ? paymentMap[paymentKey] : undefined;
    if (genPayment && treasuryAccount && totalForA3 > 0 && !isRectificativa) {
      lines.push(
        buildPaymentDueDateRecord(
          empresa, entryDate, r.supplierAccount, r.supplierName,
          documentNumber, r.invoiceNumber, totalForA3,
        ),
      );
      const payRef = documentNumber;
      const payConcept = `PAGO FRA/${cleanText(r.invoiceNumber).toUpperCase()}`.slice(0, 30);
      lines.push(
        buildSimpleEntryRecord(
          empresa, entryDate, r.supplierAccount, r.supplierName,
          "D", payRef, "I", payConcept, totalForA3,
        ),
      );
      lines.push(
        buildSimpleEntryRecord(
          empresa, entryDate, treasuryAccount, `PAGO ${paymentKey}`,
          "H", payRef, "U", payConcept, totalForA3,
        ),
      );
    }
  }

  const normalized = lines.map((l, idx) => {
    if (l.length !== PAYLOAD_LENGTH) {
      throw new Error(`SUENLACE recibidas: línea ${idx + 1} tiene ${l.length} chars (esperado ${PAYLOAD_LENGTH})`);
    }
    return l;
  });

  const totalBytes = normalized.length * LINE_LENGTH;
  const buffer = new Uint8Array(totalBytes);
  for (let i = 0; i < normalized.length; i++) {
    const payload = encodeWin1252(normalized[i]);
    buffer.set(payload, i * LINE_LENGTH);
    buffer[i * LINE_LENGTH + PAYLOAD_LENGTH] = 0x0d;
    buffer[i * LINE_LENGTH + PAYLOAD_LENGTH + 1] = 0x0a;
  }

  const fileName = `SUENLACE_REC_${empresa}_${Date.now()}.DAT`;
  return {
    bytes: buffer,
    fileName,
    invoiceCount: rows.length,
    lineCount: normalized.length,
    warnings: [...warnings, ...skipped],
  };
};
